import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/button';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  maxRetries?: number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
}

/**
 * Enhanced Error Boundary for Meal Planning Components
 * 
 * Provides graceful error handling with:
 * - User-friendly error messages
 * - Retry functionality with exponential backoff
 * - Error reporting for monitoring
 * - Fallback UI for different error types
 */
export class MealPlanErrorBoundary extends Component<Props, State> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error for monitoring
    console.error('MealPlan Error Boundary caught an error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      retryCount: this.state.retryCount
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Send error to analytics/monitoring service
    this.reportError(error, errorInfo);

    // If another error occurred while we were in a backoff window,
    // stop the backoff so user can attempt again.
    if (this.state.isRetrying) {
      this.setState({ isRetrying: false });
    }
  }

  private reportError = async (error: Error, errorInfo: ErrorInfo) => {
    try {
      // Only report in production or when analytics is enabled
      if (process.env.NODE_ENV === 'production' && (window as any).posthog) {
        (window as any).posthog.capture('meal_plan_error_boundary_triggered', {
          error_message: error.message,
          error_stack: error.stack,
          component_stack: errorInfo.componentStack,
          retry_count: this.state.retryCount,
          user_agent: navigator.userAgent,
          url: window.location.href
        });
      }
    } catch (reportingError) {
      console.warn('Failed to report error to analytics:', reportingError);
    }
  };

  private handleRetry = () => {
    const { retryCount } = this.state;
    const maxRetries = this.props.maxRetries ?? 3;
    
    if (retryCount >= maxRetries) {
      console.warn('Max retry attempts reached');
      return;
    }

    // Exponential backoff: 1s, 2s, 4s
    const delayMs = Math.pow(2, retryCount) * 1000;
    
    console.log(`Retrying meal plan component in ${delayMs}ms (attempt ${retryCount + 1}/${maxRetries})`);
    
    // Increment retry immediately so UI reflects attempt count
    this.setState({ isRetrying: true, retryCount: retryCount + 1 });
    this.retryTimeout = setTimeout(() => {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        isRetrying: false
      });
    }, delayMs);
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    });
  };

  private getErrorType = (error: Error): 'network' | 'data' | 'rendering' | 'unknown' => {
    const message = error.message.toLowerCase();
    
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return 'network';
    }
    
    if (message.includes('undefined') || message.includes('null') || message.includes('data')) {
      return 'data';
    }
    
    if (message.includes('render') || message.includes('element') || message.includes('component')) {
      return 'rendering';
    }
    
    return 'unknown';
  };

  private getUserFriendlyMessage = (errorType: string): string => {
    switch (errorType) {
      case 'network':
        return "We're having trouble connecting to our meal planning service. Please check your internet connection and try again.";
      case 'data':
        return "There was an issue loading your meal plan data. This might be temporary - please try refreshing.";
      case 'rendering':
        return "Something went wrong displaying your meal plan. We're working on it - please try again.";
      default:
        return "Oops! Something unexpected happened with your meal plan. Don't worry, we're on it!";
    }
  };

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    // If children changed, reset error state to allow rerender
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorType = this.getErrorType(this.state.error!);
      const userMessage = this.getUserFriendlyMessage(errorType);
    const maxRetries = this.props.maxRetries ?? 3;
    const canRetry = this.state.retryCount < maxRetries;
      const isDev = process.env.NODE_ENV === 'development';

      return (
        <div className="flex flex-col items-center justify-center p-8 text-center bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          
          <h2 className="text-xl font-semibold text-red-800 mb-2">
            Something went wrong with your meal plan
          </h2>
          
          <p className="text-red-600 mb-4 max-w-md">
            {userMessage}
          </p>
          
          {canRetry && (
            <div className="flex gap-3 mb-4">
              <Button 
                onClick={this.handleRetry}
                variant="outline"
                disabled={this.state.isRetrying}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </Button>
              
              <Button 
                onClick={this.handleReset}
                variant="default"
              >
                <Home className="w-4 h-4 mr-2" />
                Start Fresh
              </Button>
            </div>
          )}
          {this.state.isRetrying && (
            <p className="text-sm text-gray-600 mb-2">Please wait</p>
          )}
          
          {!canRetry && (
            <div className="mb-4">
              <p className="text-sm text-red-500 mb-2">
                We've tried several times but the issue persists.
              </p>
              <Button 
                onClick={() => window.location.reload()}
                variant="outline"
              >
                Refresh Page
              </Button>
            </div>
          )}

          {isDev && this.state.error && (
            <details className="mt-4 text-left w-full">
              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">Show Details</summary>
              <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono">
                <p className="mb-2 font-semibold text-red-600">Error: {this.state.error.message}</p>
                
                {this.state.error.stack && (
                  <>
                    <p className="font-semibold text-red-600">Stack Trace:</p>
                    <pre className="whitespace-pre-wrap text-xs">
                      {this.state.error.stack}
                    </pre>
                  </>
                )}
                
                {this.state.errorInfo?.componentStack && (
                  <>
                    <p className="font-semibold text-red-600 mt-2">Component Stack:</p>
                    <pre className="whitespace-pre-wrap text-xs">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </>
                )}
                
                <p className="mt-2 text-gray-600">
                  Retry Count: {this.state.retryCount}/3
                </p>
              </div>
            </details>
          )}
          
          {this.state.retryCount >= maxRetries ? (
            <p className="text-sm text-red-600 mt-2">Multiple errors occurred. Please refresh the page.</p>
          ) : (
            <p className="text-xs text-gray-500 mt-4">
              If this problem continues, please contact support with error code: MP-{Date.now()}
            </p>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
